"use client";

import { useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { AuthDialog } from "@/components/auth/AuthDialog";
import type { Dictionary, Locale } from "@/i18n/dictionaries";
import styles from "./HomeUploadDialog.module.css";

type UploadDialogLabels = {
  title: string;
  chooseFile: string;
  notesTitle: string;
  fileType: string;
  fileSize: string;
  modelSize: string;
  declaration: string;
  wrongType: string;
  fileTooLarge: string;
  unavailable: string;
  creating: string;
  close: string;
};

type DatabaseRenderDraft = {
  id: string;
  fileName: string;
  fileSize: number;
  createdAt: string;
  status: "database-draft";
  config: {
    activeTab: "gem" | "metal" | "hdr";
    metalColor: string;
    gemColor: string;
    hdr: string;
    notes: string;
  };
};

type DatabaseReadiness = {
  enabled: boolean;
  reason: string | null;
  status: number;
};

type Props = {
  children: ReactNode;
  authLabels: Dictionary["authPage"];
  labels: UploadDialogLabels;
  locale: Locale;
  triggerClassName?: string;
};

async function createDatabaseDraft(file: File): Promise<DatabaseRenderDraft> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch("/api/render/persistence/uploads", { method: "POST", body: formData });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("RENDER_AUTH_REQUIRED");
    }
    const failure = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(failure?.message || "DATABASE_UPLOAD_UNAVAILABLE");
  }

  const payload = (await response.json()) as {
    record?: { id?: string };
    upload?: { originalFileName?: string; fileSizeBytes?: number; createTime?: string };
    snapshot?: { configJson?: string | null };
  };
  if (!payload.record?.id) {
    throw new Error("DATABASE_UPLOAD_UNAVAILABLE");
  }

  let config: DatabaseRenderDraft["config"] = { activeTab: "metal", metalColor: "#d8d8d8", gemColor: "#67c8ff", hdr: "studio-soft", notes: "" };
  try {
    config = { ...config, ...(JSON.parse(payload.snapshot?.configJson || "{}") as Partial<DatabaseRenderDraft["config"]>) };
  } catch {
    // The DB record remains valid; use the editor defaults when an old config is malformed.
  }

  return {
    id: payload.record.id,
    fileName: payload.upload?.originalFileName || file.name,
    fileSize: payload.upload?.fileSizeBytes || file.size,
    createdAt: payload.upload?.createTime || new Date().toISOString(),
    status: "database-draft",
    config,
  };
}

async function getDatabaseReadiness(): Promise<DatabaseReadiness> {
  const response = await fetch("/api/render/persistence/uploads/readiness", { cache: "no-store" }).catch(() => null);
  if (!response) {
    return { enabled: false, reason: null, status: 0 };
  }
  const payload = (await response.json().catch(() => null)) as { enabled?: boolean; reason?: string; message?: string } | null;

  if (response.ok && payload?.enabled) {
    return { enabled: true, reason: null, status: response.status };
  }

  return {
    enabled: false,
    reason: payload?.reason || payload?.message || "Database-backed render upload is unavailable.",
    status: response.status,
  };
}

export function HomeUploadDialog({ children, authLabels, labels, locale, triggerClassName }: Props) {
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [error, setError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  function close() {
    if (isCreating) {
      return;
    }

    setIsOpen(false);
    setError("");
  }

  async function openUpload() {
    setError("");
    const readiness = await getDatabaseReadiness();
    if (readiness.enabled) {
      setIsOpen(true);
      return;
    }

    if (readiness.status === 401) {
      setAuthOpen(true);
      return;
    }

    setIsOpen(true);
    setError(labels.unavailable);
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".fbx")) {
      setError(labels.wrongType);
      return;
    }
    if (file.size > 64 * 1024 * 1024) {
      setError(labels.fileTooLarge);
      return;
    }

    setIsCreating(true);
    setError("");

    const readiness = await getDatabaseReadiness();
    if (!readiness.enabled) {
      if (readiness.status === 401) {
        setIsOpen(false);
        setAuthOpen(true);
      } else {
        setError(labels.unavailable);
      }
      setIsCreating(false);
      return;
    }

    try {
      const databaseDraft = await createDatabaseDraft(file);
      router.push(`/render-editor/${encodeURIComponent(databaseDraft.id)}?lang=${locale}`);
      return;
    } catch (exception) {
      if (exception instanceof Error && exception.message === "RENDER_AUTH_REQUIRED") {
        setIsOpen(false);
        setAuthOpen(true);
        return;
      }
      setError(labels.unavailable);
      setIsCreating(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={`${triggerClassName ?? ""} ${styles.trigger}`}
        onClick={openUpload}
      >
        {children}
      </button>

      {authOpen ? (
        <AuthDialog
          locale={locale}
          labels={authLabels}
          mode="login"
          returnUrl={`/?lang=${locale}`}
          onClose={() => setAuthOpen(false)}
        />
      ) : null}

      {isOpen ? (
        <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label={labels.title}>
          <div className={styles.dialog}>
            <div className={styles.header}>
              <button type="button" className={styles.closeButton} onClick={close} aria-label={labels.close}>
                x
              </button>
            </div>
            <div className={styles.uploadPanel}>
              <p className={styles.title}>{isCreating ? labels.creating : labels.title}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".fbx"
                className={styles.fileInput}
                onChange={handleFileChange}
              />
              <button
                type="button"
                className={styles.pickButton}
                onClick={() => fileInputRef.current?.click()}
                disabled={isCreating}
              >
                <span className={styles.handIcon}>UP</span>
                <span>{labels.chooseFile}</span>
              </button>
              {error ? <p className={styles.error}>{error}</p> : null}
            </div>
            <div className={styles.notes}>
              <h2>{labels.notesTitle}</h2>
              <p>{labels.fileType}</p>
              <p>{labels.fileSize}</p>
              <p>{labels.modelSize}</p>
            </div>
            <p className={styles.declaration}>{labels.declaration}</p>
          </div>
        </div>
      ) : null}
    </>
  );
}
